import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Megaphone,
  Plus,
  Settings,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  BarChart3,
  Users,
  Target,
  Globe,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  X,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MessageSquare,
  Printer,
  FileText,
  Check,
  Search,
  Image as ImageIcon,
} from "lucide-react";

import compaignImage from "../../assets/compaign.png";
import {
  getCampaignProperties,
  createCampaign,
  getCampaigns,
  sendPropertyToPrintForce,
  setPrintForceCampaignId
} from "../../api/compaign";

const CampaignManager = () => {
  const navigate = useNavigate();
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  // Property id currently being sent to PrintForce, so only that one card
  // shows a spinner.
  const [printForceBusy, setPrintForceBusy] = useState(null);
  // Whether PrintForce have switched their import on. Shown once for the page,
  // because while it is off it is true of every listing.
  const [printForceFeedEnabled, setPrintForceFeedEnabled] = useState(true);
  // The card whose Media Wizard campaign id is being edited, and the value
  // being typed. Only one at a time: this is a rare, deliberate action.
  const [campaignIdFor, setCampaignIdFor] = useState(null);
  const [campaignIdDraft, setCampaignIdDraft] = useState("");
  const [campaignIdSaving, setCampaignIdSaving] = useState(false);
  // Address search. `search` is what is typed, `appliedSearch` is what the
  // server has been asked for - debounced, so a page load does not fire on
  // every keystroke. Christian Gravias, 28 Aug 2026.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [stats, setStats] = useState({ total_count: 0, page: 1, total_pages: 1 });

  // Create Campaign Form State
  const [formData, setFormData] = useState({
    name: "",
    property_id: "",
    campaign_type: "social",
    budget: "",
    description: "",
    platforms: [],
    target_audience: {
      location: "",
      age_range: "25-55",
      interests: []
    },
    content_assets: [],
    manager_name: "",
    manager_image: null
  });

  const selectedProperty = allProperties.find(p => String(p.id) === String(formData.property_id));

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats.page, appliedSearch]);

  // Debounce the typing, and go back to page 1: staying on page 5 of the old
  // result set shows an empty grid and reads as "no matches".
  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => (prev === next ? prev : next));
      setStats((s) => (s.page === 1 ? s : { ...s, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const query = { page: stats.page, page_size: 8 };
      if (appliedSearch) query.search = appliedSearch;
      const [propRes, allPropRes] = await Promise.all([
        getCampaignProperties(query),
        // The wizard's picker searches the whole list itself, so it is never
        // narrowed by the grid's search box.
        getCampaignProperties({ all: true })
      ]);

      setProperties(propRes.data.properties || []);
      setPrintForceFeedEnabled(propRes.data.printforce_feed_enabled !== false);
      setAllProperties(allPropRes.data.properties || []);
      setStats({
        total_count: propRes.data.total_count,
        page: propRes.data.page,
        total_pages: propRes.data.total_pages
      });
    } catch (error) {
      console.error("Error fetching campaign data:", error);
      toast.error("Failed to load campaign data");
    } finally {
      setLoading(false);
    }
  };

  // Christian Gravias, 27 Aug 2026: "Change the heading of the property to be
  // the property address" and, for the wizard, "When you select a property,
  // please put the address of the property". The marketing headline
  // ("A Rare Family Residence with Flexible Living...") tells nobody which
  // house it is. Fall back through address, then suburb, then the headline, so
  // a record with no address still reads as something.
  const propertyAddress = (p) =>
    p?.full_address || p?.address || p?.suburb || p?.title || "Selected Property";

  // Christian Gravias, 28 Aug 2026: "Add SMS Buyers button to this list. If you
  // click Email or SMS button please create an auto template of this property
  // being just listed." One hand-off for both channels; the composer reads
  // template=just_listed and writes the message from the listing itself.
  const openBuyerComms = (property, channel) =>
    navigate(
      `/communications/campaigns/new?property_id=${property.id}` +
      `&label=${encodeURIComponent(propertyAddress(property))}` +
      `&channel=${channel}&template=just_listed`
    );

  const formatSentAt = (iso) => {
    if (!iso) return "";
    const when = new Date(iso);
    if (isNaN(when.getTime())) return "";
    return when.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  };

  // PrintForce send us nothing back, so their campaign id can only reach us if
  // somebody types it. An agent already looking at the campaign in Media Wizard
  // copies the number off the end of the address bar, and that listing gets an
  // exact Campaign Details link from then on. Kim Blyth's redirect (28 Aug 2026)
  // will cover the rest, but only for campaigns that have started, and it does
  // not exist yet.
  const openCampaignIdEditor = (property) => {
    setCampaignIdFor(property.id);
    setCampaignIdDraft(property.printforce?.campaign_id || "");
  };

  const closeCampaignIdEditor = () => {
    setCampaignIdFor(null);
    setCampaignIdDraft("");
  };

  const handleSaveCampaignId = async (property) => {
    const typed = (campaignIdDraft || "").trim();
    // Paste the whole URL and we can still find the id, which is what people
    // actually do. .../campaign/view/id/204413/ -> 204413.
    const fromUrl = typed.match(/(\d+)\/?\s*$/);
    const campaignId = fromUrl ? fromUrl[1] : typed;

    if (campaignId && !/^\d+$/.test(campaignId)) {
      toast.error("Paste just the number from the end of the Media Wizard address bar");
      return;
    }

    setCampaignIdSaving(true);
    try {
      const res = await setPrintForceCampaignId(property.id, campaignId);
      const next = res.data || {};
      // Patch the row in place rather than reloading the page: this changes one
      // link on one card and a refetch would lose the reader's scroll position.
      const applyTo = (list) => list.map((row) => (
        row.id === property.id
          ? {
              ...row,
              printforce: {
                ...row.printforce,
                campaign_id: next.campaign_id || "",
                campaign_url: next.campaign_url || row.printforce?.campaign_url || "",
                campaign_url_is_exact: !!next.campaign_url_is_exact,
              },
            }
          : row
      ));
      setProperties(applyTo);
      setAllProperties(applyTo);
      toast.success(campaignId ? "Campaign linked" : "Campaign link removed");
      closeCampaignIdEditor();
    } catch (error) {
      console.error("Error saving the Media Wizard campaign id:", error);
      toast.error(
        error.response?.data?.error || "Could not save that campaign id"
      );
    } finally {
      setCampaignIdSaving(false);
    }
  };

  // The Vault behaviour Christian asked for: pick the property, it goes to
  // PrintForce. The feed sends changes automatically anyway, so this is a
  // deliberate push that always goes, plus an answer either way.
  //
  // `openOrderPage` is the 28 Aug 2026 half: "When I click send to printforce
  // cant it just go into the page to create the brochure & board for the
  // campaign." Sending is only the hand-off - the board and the brochure are
  // ordered in PrintForce's Media Wizard, so a successful send now carries the
  // agent straight there instead of leaving them on a toast.
  const handleSendToPrintForce = async (property, { openOrderPage = false } = {}) => {
    // Opened synchronously, before the await: a window.open() that runs after
    // one is treated as unrequested by the browser and silently blocked. No
    // "noopener" feature string here - passing it makes window.open() return
    // null, so there would be no tab to point anywhere. The opener reference is
    // severed on the tab itself instead, which does the same job.
    const tab = openOrderPage ? window.open("", "_blank") : null;
    if (tab) {
      try {
        tab.opener = null;
      } catch {
        // Some browsers refuse the assignment. Harmless; the tab still opens.
      }
    }
    setPrintForceBusy(property.id);
    try {
      const res = await sendPropertyToPrintForce(property.id);
      const result = res.data || {};
      if (result.sent) {
        toast.success(result.message || "Sent to PrintForce");
      } else {
        // Usually not an error. The common case is that PrintForce have not
        // switched the feed on at their end yet, and the message says so.
        toast(result.message || "Nothing was sent to PrintForce", { icon: "ℹ️", duration: 6000 });
      }
      const orderUrl = result.campaign_url || property.printforce?.campaign_url || "";
      if (tab && result.sent && orderUrl) {
        tab.location = orderUrl;
        // PrintForce import on their own schedule (about five minutes), and
        // their page only opens a campaign that has already been STARTED at
        // their end. Otherwise it drops the agent on a search for the street.
        // Say so rather than letting it read as a broken link.
        if (!result.campaign_url_is_exact) {
          toast(
            "Opening PrintForce. If the campaign has not been started at their end yet, you will land on a search for this street instead.",
            { icon: "🖨️", duration: 8000 }
          );
        }
      } else if (tab) {
        tab.close();
      }
      fetchData();
    } catch (error) {
      if (tab) tab.close();
      console.error("Error sending property to PrintForce:", error);
      toast.error(
        error.response?.data?.error || "Could not send this property to PrintForce"
      );
    } finally {
      setPrintForceBusy(null);
    }
  };

  const handleCreateCampaign = async (e) => {
    if (e) e.preventDefault();

    if (!formData.property_id) {
      toast.error("Please select a property for the campaign");
      setSetupStep(1);
      return;
    }

    try {
      // Use FormData for file upload support
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('property_id', formData.property_id);
      submitData.append('campaign_type', formData.campaign_type);
      submitData.append('budget', formData.budget === "" ? "" : formData.budget);
      submitData.append('description', formData.description);
      submitData.append('platforms', JSON.stringify(formData.platforms));
      submitData.append('target_audience', JSON.stringify(formData.target_audience));
      submitData.append('manager_name', formData.manager_name);
      if (formData.manager_image) {
        submitData.append('manager_image', formData.manager_image);
      }

      const res = await createCampaign(submitData);
      toast.success("Campaign created and setup successfully!");

      // A print campaign hands the property to PrintForce. Say what happened,
      // otherwise the hand-off is invisible and looks like it did not run.
      const printforce = res?.data?.printforce;
      if (printforce) {
        if (printforce.sent) {
          toast.success(printforce.message, { duration: 6000 });
        } else {
          toast(printforce.message, { icon: "ℹ️", duration: 6000 });
        }
      }
      setShowCampaignModal(false);
      resetForm();
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error creating campaign:", error);
      const errors = error.response?.data;
      if (typeof errors === 'object') {
        Object.values(errors).flat().forEach(err => toast.error(String(err)));
      } else {
        toast.error("Failed to create campaign");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      property_id: "",
      campaign_type: "social",
      budget: "",
      description: "",
      platforms: [],
      target_audience: {
        location: "",
        age_range: "25-55",
        interests: []
      },
      content_assets: [],
      manager_name: "",
      manager_image: null
    });
    setSetupStep(1);
  };

  const togglePlatform = (platform) => {
    const nextPlatforms = formData.platforms.includes(platform)
      ? formData.platforms.filter(p => p !== platform)
      : [...formData.platforms, platform];
    setFormData({ ...formData, platforms: nextPlatforms });
  };

  const toggleInterest = (interest) => {
    const nextInterests = formData.target_audience.interests.includes(interest)
      ? formData.target_audience.interests.filter(i => i !== interest)
      : [...formData.target_audience.interests, interest];
    setFormData({
      ...formData,
      target_audience: { ...formData.target_audience, interests: nextInterests }
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= stats.total_pages) {
      setStats(prev => ({ ...prev, page: newPage }));
    }
  };

  const nextSetupStep = () => setSetupStep(prev => Math.min(prev + 1, 4));
  const prevSetupStep = () => setSetupStep(prev => Math.max(prev - 1, 1));

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center">
                <Megaphone className="w-8 h-8 mr-3 text-[#55C5D0]" />
                Campaign Manager
              </h1>
              <p className="text-slate-600 mt-2">
                Manage marketing campaigns and third-party integrations
              </p>
            </div>
            <button
              onClick={() => { resetForm(); setShowCampaignModal(true); }}
              className="bg-[#55C5D0] text-white px-6 py-3 rounded-lg hover:bg-[#3bb3be] transition-colors flex items-center shadow-lg transform active:scale-95 duration-200"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Campaign
            </button>
          </div>
        </div>

        {/* Said once for the whole page. PrintForce enabled their end on
            27 Aug 2026, so the remaining switch is ours. Do not word this as a
            PrintForce problem. */}
        {!printForceFeedEnabled && (
          <div className="mb-4 flex items-start p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Printer className="w-5 h-5 mr-3 mt-0.5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-bold">Automatic sending to PrintForce is switched off.</span>{" "}
              PrintForce have enabled their end and already hold the listings we have
              sent them. Until the feed is switched on here, new listings and edits will
              not go across on their own.
            </p>
          </div>
        )}

        {/* The search box sits on its own full-width row above Active
            Properties. It used to share the heading strip, right-aligned and
            sm:w-80, where it read as part of the chrome and was easy to miss
            (Christian, 1 Sep 2026). */}
        <div className="relative mb-4">
          <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search an address or suburb"
            aria-label="Search properties by address or suburb"
            className="w-full bg-white border-2 border-slate-200 rounded-xl pl-12 pr-12 py-3.5 text-base text-slate-700 placeholder-slate-400 shadow-sm focus:outline-none focus:border-[#55C5D0]"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              title="Clear the search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content Space (No Tabs) */}
        <div className="bg-[#FEFEFE] border-[1.07px] border-[#E8EDF3] rounded-xl space-y-0 overflow-hidden shadow-sm">

          {/* Recent Header Strip */}
          <div className="min-h-[49px] border-b-[1.07px] border-[#E7E7E7] bg-[#FFFFFF] px-6 py-2 flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-bold text-slate-900">Active Properties</h2>
          </div>

          <div className="p-6 space-y-8 bg-[#F9FAFB]">
            {/* Property Campaigns Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <div key={i} className="h-72 bg-white rounded-xl shadow-sm border border-slate-200 animate-pulse" />
                ))
              ) : properties.length > 0 ? (
                properties.map((property) => (
                  <div key={property.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 group">
                    {/* Card Image */}
                    <div className="relative h-48 w-full overflow-hidden">
                      <img
                        src={property.images?.[0] || compaignImage}
                        alt={propertyAddress(property)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      {/* Sale Tag */}
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#55C5D0] text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg uppercase">{property.status || 'SALE'}</span>
                      </div>
                      {/* Property Details Tag */}
                      <div className="absolute bottom-4 right-4">
                        <button className="bg-[#697D83]/90 backdrop-blur-sm text-white text-[10px] px-3 py-1.5 rounded-lg flex items-center hover:bg-[#586b70] transition-colors">
                          Property Details
                        </button>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 mr-2">
                          <h3
                            className="font-bold text-slate-900 text-sm mb-1 line-clamp-2 group-hover:text-[#55C5D0] transition-colors"
                            title={propertyAddress(property)}
                          >
                            {propertyAddress(property)}
                          </h3>
                        </div>
                        <div className="flex flex-col items-center flex-shrink-0">
                          <img
                            src={
                              (property.has_campaign && property.campaign_manager_image)
                                ? property.campaign_manager_image
                                : (property.agent?.profile_pic || `https://ui-avatars.com/api/?name=${encodeURIComponent(property.agent?.name || 'A')}&background=random`)
                            }
                            alt={property.has_campaign ? property.campaign_manager_name : property.agent?.name}
                            className="w-8 h-8 rounded-full mb-1 object-cover ring-2 ring-slate-100"
                          />
                          <span className="text-[8px] text-slate-400 text-center w-12 truncate">
                            {property.has_campaign && property.campaign_manager_name
                              ? property.campaign_manager_name
                              : property.agent?.name}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setFormData({
                            ...formData,
                            property_id: property.id,
                            name: `Campaign for ${propertyAddress(property)}`,
                            target_audience: { ...formData.target_audience, location: property.suburb }
                          });
                          setSetupStep(1);
                          setShowCampaignModal(true);
                        }}
                        className={`w-full border rounded-lg py-2 text-sm font-medium flex items-center justify-center transition-all duration-300 ${property.has_campaign
                          ? 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          : 'border-[#55C5D0] text-[#55C5D0] hover:bg-[#55C5D0] hover:text-white'
                          }`}
                      >
                        {property.has_campaign ? 'Active Campaign' : 'Start Campaign'}
                        <Play className={`w-3 h-3 ml-2 ${property.has_campaign ? 'fill-slate-400' : 'fill-current'}`} />
                      </button>

                      {/* Hand-off to the Communications Hub: reach this listing's
                          interested buyers through the real email/SMS send engine.

                          Both carry template=just_listed, so the composer opens
                          with this property's Just Listed message already
                          written - Christian Gravias, 28 Aug 2026: "If you click
                          Email or SMS button please create an auto template of
                          this property being just listed." */}
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => openBuyerComms(property, "email")}
                          title="Email the buyers interested in this listing"
                          className="border border-slate-200 rounded-lg py-2 px-1 text-xs font-medium flex items-center justify-center whitespace-nowrap text-slate-600 hover:bg-slate-50 transition-all duration-300"
                        >
                          Email buyers
                          <Mail className="w-3 h-3 ml-1.5 flex-shrink-0" />
                        </button>
                        <button
                          onClick={() => openBuyerComms(property, "sms")}
                          title="Text the buyers interested in this listing"
                          className="border border-slate-200 rounded-lg py-2 px-1 text-xs font-medium flex items-center justify-center whitespace-nowrap text-slate-600 hover:bg-slate-50 transition-all duration-300"
                        >
                          SMS buyers
                          <MessageSquare className="w-3 h-3 ml-1.5 flex-shrink-0" />
                        </button>
                      </div>

                      {/* PrintForce, the print and signage vendor. Hidden for
                          listings their feed will not accept (sold, leased,
                          appraisals) so nothing here is a dead end.

                          Once PrintForce hold the listing the primary action
                          opens their Media Wizard in a new tab, which is the
                          Vault behaviour Christian asked for. Resending is
                          demoted to a text link: it matters rarely, and four
                          equal buttons on a card is a wall.

                          Both states say "Order Board & Brochures" (Christian
                          Gravias, 28 Aug 2026). They are the same errand - one
                          hands the listing over first - and it was the old
                          "Campaign Details" wording that made the page look
                          like it had two different print buttons. */}
                      {property.printforce?.eligible !== false && (
                        <>
                          {property.printforce?.sent ? (
                            <>
                              <a
                                href={property.printforce?.campaign_url || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-full mt-2 border rounded-lg py-2 text-sm font-medium flex items-center justify-center transition-all duration-300 ${property.printforce?.campaign_url
                                  ? 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                  : 'border-slate-100 text-slate-300 pointer-events-none'
                                  }`}
                              >
                                Order Board &amp; Brochures
                                <ExternalLink className="w-3 h-3 ml-2" />
                              </a>

                              {/* The link is only exact once somebody has told
                                  us Media Wizard's own campaign id. Until then
                                  say so, and offer the one-time paste that
                                  fixes it for this listing. */}
                              {campaignIdFor === property.id ? (
                                <div className="mt-1">
                                  <input
                                    autoFocus
                                    value={campaignIdDraft}
                                    onChange={(e) => setCampaignIdDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") handleSaveCampaignId(property);
                                      if (e.key === "Escape") closeCampaignIdEditor();
                                    }}
                                    placeholder="Media Wizard campaign id, e.g. 204413"
                                    className="w-full border border-slate-200 rounded-lg px-2 py-1 text-[11px] text-slate-600 focus:outline-none focus:border-[#55C5D0]"
                                  />
                                  <div className="mt-1 flex items-center justify-center gap-3 text-[10px]">
                                    <button
                                      onClick={() => handleSaveCampaignId(property)}
                                      disabled={campaignIdSaving}
                                      className="text-[#55C5D0] hover:underline disabled:opacity-50"
                                    >
                                      {campaignIdSaving ? "Saving..." : "Save"}
                                    </button>
                                    <button
                                      onClick={closeCampaignIdEditor}
                                      disabled={campaignIdSaving}
                                      className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : property.printforce?.campaign_url_is_exact === false ? (
                                <div className="mt-1 text-center text-[10px] text-slate-400">
                                  Opens the campaign, or a search if not
                                  started.{" "}
                                  <button
                                    onClick={() => openCampaignIdEditor(property)}
                                    className="text-[#55C5D0] hover:underline"
                                  >
                                    Link the campaign
                                  </button>
                                </div>
                              ) : property.printforce?.campaign_id ? (
                                <div className="mt-1 text-center text-[10px] text-slate-400">
                                  Campaign {property.printforce.campaign_id}.{" "}
                                  <button
                                    onClick={() => openCampaignIdEditor(property)}
                                    className="text-[#55C5D0] hover:underline"
                                  >
                                    Change
                                  </button>
                                </div>
                              ) : null}

                              <div className="mt-1 flex items-center justify-center text-[10px] text-slate-400">
                                <CheckCircle className="w-3 h-3 mr-1 text-emerald-500" />
                                With PrintForce since {formatSentAt(property.printforce.sent_at)}
                              </div>

                              <button
                                onClick={() => handleSendToPrintForce(property)}
                                disabled={printForceBusy === property.id}
                                className="w-full mt-1 text-[10px] text-slate-400 hover:text-[#55C5D0] disabled:opacity-50 transition-colors"
                              >
                                {printForceBusy === property.id
                                  ? "Resending..."
                                  : "Resend their copy"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleSendToPrintForce(property, { openOrderPage: true })}
                              disabled={printForceBusy === property.id}
                              title="Sends this listing to PrintForce and opens their Media Wizard to order the board and brochures"
                              className="w-full mt-2 border border-slate-200 rounded-lg py-2 text-sm font-medium flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-all duration-300"
                            >
                              {printForceBusy === property.id
                                ? "Sending to PrintForce..."
                                : "Order Board & Brochures"}
                              {printForceBusy === property.id
                                ? <Loader2 className="w-3 h-3 ml-2 animate-spin" />
                                : <Printer className="w-3 h-3 ml-2" />}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-20 text-center text-slate-500 bg-white rounded-xl border-2 border-dashed border-slate-200">
                  <Megaphone className="w-16 h-16 mx-auto mb-4 opacity-10 text-[#55C5D0]" />
                  {appliedSearch ? (
                    <>
                      <p className="text-lg font-medium text-slate-600">
                        Nothing matches "{appliedSearch}"
                      </p>
                      <p className="text-sm text-slate-400 mt-2">
                        This searches the properties you are currently selling. Sold, leased
                        and appraisal records are not on this page.{" "}
                        <button onClick={() => setSearch("")} className="text-[#55C5D0] hover:underline">
                          Clear the search
                        </button>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-medium text-slate-600">No properties available for campaigns</p>
                      <p className="text-sm text-slate-400 mt-2">Check your property listings to ensure they are active.</p>
                    </>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Pagination Footer */}
          <div className="h-[56px] border-t-[1.07px] border-[#E7E7E7] bg-white px-6 flex items-center justify-between">
            <button
              disabled={stats.page === 1}
              onClick={() => handlePageChange(stats.page - 1)}
              className="flex items-center text-slate-600 text-sm font-medium hover:text-[#55C5D0] px-4 py-2 rounded-lg hover:bg-slate-50 disabled:opacity-30 transition-all"
            >
              <ChevronLeft className="w-5 h-5 mr-1" /> Previous
            </button>

            <div className="text-sm font-medium text-slate-500">
              Page <span className="text-[#55C5D0]">{stats.page}</span> of {stats.total_pages}
              <span className="ml-3 text-slate-300 font-normal">|</span>
              <span className="ml-3 text-slate-400 font-normal">{stats.total_count} properties total</span>
            </div>

            <button
              disabled={stats.page === stats.total_pages}
              onClick={() => handlePageChange(stats.page + 1)}
              className="flex items-center text-slate-600 text-sm font-medium hover:text-[#55C5D0] px-4 py-2 rounded-lg hover:bg-slate-50 disabled:opacity-30 transition-all"
            >
              Next <ChevronRight className="w-5 h-5 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Create Modal (Wizard) */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-[#55C5D0] text-white rounded-xl flex items-center justify-center shadow-lg shadow-[#55C5D0]/20">
                  <Settings className="w-6 h-6 animate-spin-slow" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Campaign Setup Wizard
                  </h3>
                  <p className="text-xs text-slate-500">Step {setupStep} of 4: {
                    setupStep === 1 ? 'General Information' :
                      setupStep === 2 ? 'Choose Platforms' :
                        setupStep === 3 ? 'Target Audience' : 'Content Selection'
                  }</p>
                </div>
              </div>
              <button
                onClick={() => setShowCampaignModal(false)}
                className="p-2 rounded-xl hover:bg-white hover:shadow-md transition-all text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-1 bg-slate-100">
              <div
                className="h-full bg-[#55C5D0] transition-all duration-500 ease-out"
                style={{ width: `${(setupStep / 4) * 100}%` }}
              ></div>
            </div>

            {/* Modal Body */}
            <div className="p-8 overflow-y-auto flex-1 bg-white">
              {setupStep === 1 && (
                <div className="space-y-6 animate-in fade-in duration-500">
                  {!selectedProperty ? (
                    <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 mb-6">
                      <p className="text-sm text-amber-700 font-medium flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Please select a property to continue
                      </p>
                    </div>
                  ) : (
                    <div className="bg-[#55C5D0]/5 p-4 rounded-2xl border border-[#55C5D0]/10 mb-6">
                      <p className="text-sm text-slate-600 flex items-center">
                        <Target className="w-4 h-4 mr-2 text-[#55C5D0]" />
                        Setting up a campaign for <span className="font-bold text-slate-900 ml-1">{propertyAddress(selectedProperty)}</span>
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight">Select Property</label>
                    <div className="relative">
                      <select
                        value={formData.property_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          const prop = [...allProperties, ...properties].find(p => String(p.id) === String(val));
                          setFormData({
                            ...formData,
                            property_id: val,
                            name: prop ? `Campaign for ${propertyAddress(prop)}` : "",
                            target_audience: { ...formData.target_audience, location: prop?.suburb || "" }
                          });
                        }}
                        className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium transition-all appearance-none cursor-pointer"
                      >
                        <option value="">{loading ? "Loading properties..." : "-- Choose a Property --"}</option>
                        {[...allProperties, ...properties]
                          .filter((p, index, self) => index === self.findIndex((t) => t.id === p.id)) // Deduplicate
                          .map(p => (
                            <option key={p.id} value={p.id}>{propertyAddress(p)}</option>
                          ))
                        }
                      </select>
                      {loading && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-5 h-5 animate-spin text-[#55C5D0]" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight">Campaign Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium transition-all"
                      placeholder="e.g. Premium Social Blast - 123 Example Rd"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight">Strategy Type</label>
                      <select
                        value={formData.campaign_type}
                        onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium transition-all appearance-none cursor-pointer"
                      >
                        <option value="social">Social Media Blitz</option>
                        <option value="email">Email Marketing</option>
                        <option value="digital">Digital Advertising</option>
                        <option value="print">Print & Local Marketing</option>
                        <option value="combined">Comprehensive Multi-Channel</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight">Budget Capacity ($)</label>
                      <input
                        type="number"
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium transition-all"
                        placeholder="500.00"
                      />
                    </div>
                  </div>
                </div>
              )}

              {setupStep === 2 && (
                <div className="space-y-6 animate-in slide-in-from-right duration-500">
                  <div className="text-center mb-8">
                    <h4 className="text-xl font-bold text-slate-900">Select Marketing Channels</h4>
                    <p className="text-slate-500 text-sm mt-2">Where would you like to promote this property?</p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                    {[
                      { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
                      { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
                      { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
                      { id: 'google', name: 'Google Ads', icon: Globe, color: '#4285F4' },
                      { id: 'email', name: 'Email Blast', icon: Mail, color: '#EA4335' },
                      { id: 'print', name: 'Brochures/Print', icon: Printer, color: '#666666' }
                    ].map((platform) => (
                      <div
                        key={platform.id}
                        onClick={() => togglePlatform(platform.id)}
                        className={`p-6 rounded-3xl border-2 transition-all cursor-pointer flex flex-col items-center justify-center space-y-4 group relative overflow-hidden ${formData.platforms.includes(platform.id)
                          ? 'border-[#55C5D0] bg-[#55C5D0]/5 shadow-lg shadow-[#55C5D0]/10'
                          : 'border-slate-50 bg-slate-50/50 hover:bg-slate-100 hover:border-slate-200'
                          }`}
                      >
                        {formData.platforms.includes(platform.id) && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#55C5D0] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <platform.icon className={`w-10 h-10 transition-colors ${formData.platforms.includes(platform.id) ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'
                          }`} style={{ color: formData.platforms.includes(platform.id) ? platform.color : undefined }} />
                        <span className="text-sm font-bold text-slate-900">{platform.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* Tells the agent the hand-off is going to happen, before it
                      does. Vault gave no warning either, but it also never left
                      anyone wondering whether the click did anything. */}
                  {(formData.platforms.includes('print')
                    || ['print', 'signage', 'combined'].includes(formData.campaign_type)) && (
                    <div className="flex items-start p-4 bg-[#55C5D0]/5 border border-[#55C5D0]/20 rounded-2xl">
                      <Printer className="w-5 h-5 mr-3 mt-0.5 text-[#55C5D0] flex-shrink-0" />
                      <p className="text-sm text-slate-600">
                        This property will be sent to <span className="font-bold text-slate-900">PrintForce</span> when
                        you create the campaign, ready for you to order brochures, boards and signage from them.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {setupStep === 3 && (
                <div className="space-y-8 animate-in slide-in-from-right duration-500">
                  <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <h5 className="text-sm font-bold text-slate-900 mb-4 uppercase flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-[#55C5D0]" /> Targeted Location
                    </h5>
                    <input
                      type="text"
                      value={formData.target_audience.location}
                      onChange={(e) => setFormData({
                        ...formData,
                        target_audience: { ...formData.target_audience, location: e.target.value }
                      })}
                      className="w-full px-5 py-4 bg-white border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium"
                      placeholder="Suburbs, cities or postcodes..."
                    />
                  </div>

                  <div>
                    <h5 className="text-sm font-bold text-slate-900 mb-4 uppercase text-left">Age Demographic</h5>
                    <div className="flex flex-wrap gap-3">
                      {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map((age) => (
                        <button
                          key={age}
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            target_audience: { ...formData.target_audience, age_range: age }
                          })}
                          className={`px-6 py-3 rounded-xl font-bold text-sm transition-all ${formData.target_audience.age_range === age
                            ? 'bg-[#55C5D0] text-white shadow-lg'
                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                            }`}
                        >
                          {age}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-bold text-slate-900 mb-4 uppercase text-left">Buyer Interests</h5>
                    <div className="flex flex-wrap gap-2 text-wrap">
                      {[
                        'First Home Buyers', 'Investors', 'Upsizers', 'Downsizers',
                        'Luxury Property', 'Suburban Living', 'City Lifestyle', 'Family Homes'
                      ].map((interest) => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleInterest(interest)}
                          className={`px-4 py-2 rounded-full border-2 text-xs font-bold transition-all ${formData.target_audience.interests.includes(interest)
                            ? 'border-[#55C5D0] bg-[#55C5D0] text-white'
                            : 'border-slate-100 bg-white text-slate-500 hover:border-slate-300'
                            }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {setupStep === 4 && (
                <div className="space-y-6 animate-in slide-in-from-right duration-500">
                  <div className="text-center mb-8">
                    <h4 className="text-xl font-bold text-slate-900">Campaign Management</h4>
                    <p className="text-slate-500 text-sm mt-2">Assign a manager and finalize settings</p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-3xl space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight">Manager Name</label>
                      <input
                        type="text"
                        value={formData.manager_name}
                        onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                        className="w-full px-5 py-4 bg-white border-none rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 font-medium"
                        placeholder="e.g. Christian Gravias"
                      />
                    </div>

                    <div>
                      <label htmlFor="manager-photo-upload" className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-tight cursor-pointer">Manager Profile Photo</label>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border-2 border-white shadow-sm">
                          {formData.manager_image ? (
                            <img
                              key={formData.manager_image instanceof File ? formData.manager_image.name : 'static'}
                              src={formData.manager_image instanceof File ? URL.createObjectURL(formData.manager_image) : formData.manager_image}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ImageIcon className="w-6 h-6 text-slate-400" />
                          )}
                        </div>
                        <input
                          id="manager-photo-upload"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setFormData({ ...formData, manager_image: e.target.files[0] });
                            }
                          }}
                          className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#55C5D0]/10 file:text-[#55C5D0] hover:file:bg-[#55C5D0]/20 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#55C5D0]/5 p-6 rounded-3xl border border-[#55C5D0]/10 text-left">
                    <h5 className="font-bold text-slate-900 mb-3 text-sm uppercase tracking-wider">Strategic Objectives</h5>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 bg-white border-none rounded-xl focus:ring-2 focus:ring-[#55C5D0]/50 outline-none text-slate-900 text-sm transition-all resize-none"
                      placeholder="Describe your primary goals..."
                    ></textarea>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-8 border-t border-slate-100 flex items-center justify-between space-x-4 bg-slate-50/50">
              <button
                type="button"
                onClick={setupStep === 1 ? () => setShowCampaignModal(false) : prevSetupStep}
                className="px-8 py-4 text-slate-500 font-bold hover:text-slate-900 transition-colors flex items-center"
              >
                {setupStep === 1 ? 'Discard' : <><ChevronLeft className="w-4 h-4 mr-2" /> Back</>}
              </button>

              <div className="flex space-x-3">
                {setupStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextSetupStep}
                    disabled={setupStep === 1 && !formData.name}
                    className="px-10 py-4 bg-[#55C5D0] text-white rounded-2xl font-bold shadow-lg shadow-[#55C5D0]/20 hover:bg-[#3bb3be] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    Continue <ChevronRight className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleCreateCampaign}
                    className="px-12 py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-lg shadow-slate-900/20 hover:bg-slate-800 transition-all flex items-center"
                  >
                    Launch Campaign <Megaphone className="w-4 h-4 ml-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManager;
